/**
 * PortfolioPanel — the portfolio level of the Diversificazione area (Phase 3).
 *
 * Reading order chosen with the user: first WHERE the portfolio is invested
 * (composition + concentration), then HOW MUCH of it is really duplicated
 * (Portfolio Impact, in portfolio points). The two rankings stay separate:
 * "most similar ETFs" is not "most redundant in my portfolio".
 *
 * Every number comes from the backend: nothing is recomputed here.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, Loader2 } from "lucide-react";
import { SectionCard } from "@/components/system/SectionCard";
import { KpiCard } from "@/components/system/KpiCard";
import { Tabs } from "@/components/system/Tabs";
import { Badge } from "@/components/ui/badge";
import { PairRankingTable } from "./PairRankingTable";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const pct = (value, digits = 1) =>
  value === null || value === undefined ? "n/d" : `${Number(value).toFixed(digits)}%`;
const pp = (value, digits = 1) =>
  value === null || value === undefined ? "n/d" : `${Number(value).toFixed(digits)} pp`;

const ASSET_TYPE_STATUS = {
  applicable: { label: "Applicabile", variant: "success" },
  not_applicable_mono_asset: { label: "N/A — portafoglio mono-asset", variant: "secondary" },
  not_supported: { label: "Dati insufficienti", variant: "danger" },
};

export function PortfolioPanel({ sessionId, dimension, onSelectPair }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [ranking, setRanking] = useState("impact");

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios
      .get(`${API}/diversification/portfolio/${sessionId}`, { params: { dimension } })
      .then(({ data: payload }) => { if (!cancelled) setData(payload); })
      .catch(() => {
        if (!cancelled) {
          setData(null);
          setError("Non è stato possibile calcolare la composizione del portafoglio.");
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sessionId, dimension]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-content-muted" data-testid="portfolio-loading">
        <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
        Calcolo del portafoglio…
      </div>
    );
  }
  if (error) return <p className="text-sm text-danger" data-testid="portfolio-error">{error}</p>;
  if (!data) return null;

  const c = data.concentration || {};
  const measurable = data.status !== "no_data";
  const orderedPairs = (ranking === "impact" ? data.ranking_by_impact : data.ranking_by_structural)
    .map((index) => data.pairs[index])
    .filter(Boolean);

  return (
    <div className="space-y-4" data-testid="portfolio-panel">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KpiCard
          testId="portfolio-classified"
          label="Classificato"
          value={pct(data.classified_pct)}
          density="compact"
        />
        <KpiCard
          testId="portfolio-unclassified"
          label="Non classificato"
          value={pct(data.unclassified_pct)}
          density="compact"
        />
        <KpiCard
          testId="portfolio-duplication"
          label="Duplicazione"
          value={pp(data.duplication_pct)}
          density="compact"
          unavailable={!measurable}
          unavailableReason="nessuna esposizione classificata"
        />
        <KpiCard
          testId="portfolio-effective-number"
          label="Esposizioni equivalenti"
          value={c.effective_number != null ? Number(c.effective_number).toFixed(2) : "n/d"}
          density="compact"
          unavailable={c.effective_number == null}
          unavailableReason="dimensione non misurabile"
        />
        <KpiCard
          testId="portfolio-hhi"
          label="HHI"
          value={c.hhi != null ? Number(c.hhi).toFixed(4) : "n/d"}
          density="compact"
          unavailable={c.hhi == null}
          unavailableReason="dimensione non misurabile"
        />
      </div>

      {(data.warnings || []).map((warning) => (
        <p
          key={warning}
          role="status"
          data-testid="portfolio-warning"
          className="flex items-start gap-2 text-label text-warning"
        >
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden="true" />
          {warning}
        </p>
      ))}

      <SectionCard
        testId="portfolio-composition"
        title={`Composizione per ${data.dimension_label}`}
        description={`Punti di portafoglio sui pesi ${data.weights_source === "user_allocation"
          ? "che hai assegnato nel tab Allocazione"
          : "equipesati (nessuna allocazione personalizzata)"}. La parte non classificata resta fuori dai numeri.`}
        density="compact"
        empty={!data.buckets.length}
        emptyTitle="Nessuna esposizione classificata"
        emptyDescription="Su questa dimensione i dati non permettono di comporre il portafoglio."
      >
        <div className="space-y-3">
          <div className="overflow-x-auto rounded-lg border border-line">
            <table className="w-full text-label" data-testid="portfolio-buckets">
              <thead>
                <tr className="bg-surface-subtle text-content-muted">
                  <th scope="col" className="px-3 py-2 text-left font-medium">Esposizione</th>
                  <th scope="col" className="px-3 py-2 text-right font-medium">Portafoglio</th>
                  <th scope="col" className="px-3 py-2 text-right font-medium">Quota classificata</th>
                  <th scope="col" className="px-3 py-2 text-right font-medium">ETF</th>
                  <th scope="col" className="px-3 py-2 text-right font-medium">Duplicato</th>
                </tr>
              </thead>
              <tbody>
                {data.buckets.map((bucket) => (
                  <tr
                    key={bucket.key}
                    className="border-t border-line"
                    data-testid={`portfolio-bucket-${bucket.key}`}
                  >
                    <td className="px-3 py-2">
                      <span className="block text-content">{bucket.label}</span>
                      {bucket.label !== bucket.key && (
                        <span className="block text-label font-mono text-content-muted">{bucket.key}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular font-medium">{pp(bucket.observed_pct, 2)}</td>
                    <td className="px-3 py-2 text-right tabular text-content-muted">
                      {pct(bucket.share_of_classified_pct, 1)}
                    </td>
                    <td className="px-3 py-2 text-right tabular">{bucket.etf_count}</td>
                    <td className="px-3 py-2 text-right tabular">
                      {bucket.duplication_pct > 0 ? pp(bucket.duplication_pct, 2) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-label text-content-muted" data-testid="portfolio-composition-reconciliation">
            {data.buckets_truncated
              ? `Mostrate ${data.buckets_truncated.shown} esposizioni su ${data.buckets_truncated.total}: `
                + `${pp(data.buckets_truncated.shown_contribution_pct, 2)} dei ${pp(data.classified_pct, 2)} classificati, `
                + `${pp(data.buckets_truncated.hidden_contribution_pct, 2)} non visualizzati.`
              : `${pp(data.classified_pct, 2)} classificati su questa dimensione + `
                + `${pp(data.unclassified_pct, 2)} non classificati = 100% del portafoglio.`}
          </p>
          <ConcentrationRow concentration={c} />
        </div>
      </SectionCard>

      {data.asset_type && (
        <SectionCard
          testId="portfolio-asset-type"
          title="Asset type del portafoglio"
          description="La diversificazione per asset type si giudica solo quando esistono davvero più asset class significative."
          density="compact"
        >
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                variant={(ASSET_TYPE_STATUS[data.asset_type.status] || ASSET_TYPE_STATUS.not_supported).variant}
                data-testid="asset-type-status"
              >
                {(ASSET_TYPE_STATUS[data.asset_type.status] || ASSET_TYPE_STATUS.not_supported).label}
              </Badge>
              {(data.asset_type.flags || []).includes("liquidity_heavy") && (
                <Badge variant="warning" data-testid="asset-type-flag-liquidity">
                  liquidità rilevante ({pct(data.asset_type.liquidity_pct, 1)})
                </Badge>
              )}
            </div>
            <p className="text-label text-content-muted" data-testid="asset-type-reason">
              {data.asset_type.reason}. Soglia di significatività{" "}
              {pct(data.asset_type.significance_threshold_pct, 0)}, soglia dedicata della liquidità{" "}
              {pct(data.asset_type.liquidity_significance_threshold_pct, 0)}.
            </p>
            <p className="text-label text-content-muted" data-testid="asset-type-significant">
              Asset class significative:{" "}
              {data.asset_type.significant_asset_types.length
                ? data.asset_type.significant_asset_types.join(", ")
                : "nessuna"}
            </p>
          </div>
        </SectionCard>
      )}

      <SectionCard
        testId="portfolio-redundancy"
        title="Ridondanza reale"
        description="Due domande diverse: quali ETF si assomigliano (strutturale) e quali producono davvero duplicazione dati i pesi che hai assegnato (punti di portafoglio)."
        density="compact"
        empty={!data.pairs.length}
        emptyTitle="Serve più di un ETF"
        emptyDescription="La ridondanza si misura fra coppie di ETF."
      >
        <div className="space-y-3">
          <Tabs
            testId="redundancy-ranking-tabs"
            variant="pill"
            ariaLabel="Ordinamento della ridondanza"
            value={ranking}
            onChange={setRanking}
            items={[
              { id: "impact", label: "Impatto sul portafoglio", testId: "ranking-tab-impact" },
              { id: "structural", label: "Sovrapposizione strutturale", testId: "ranking-tab-structural" },
            ]}
          />
          <PairRankingTable
            pairs={orderedPairs}
            mode={ranking}
            onSelectPair={onSelectPair}
          />
          {data.top_duplicated.length > 0 && (
            <div className="space-y-1">
              <h3 className="text-sm font-medium text-content-secondary">
                Esposizioni comprate da più ETF
              </h3>
              <ul className="space-y-1" data-testid="portfolio-top-duplicated">
                {data.top_duplicated.map((bucket) => (
                  <li
                    key={bucket.key}
                    className="flex flex-wrap items-baseline justify-between gap-2 text-label border-b border-line py-1"
                    data-testid={`portfolio-duplicated-${bucket.key}`}
                  >
                    <span className="text-content">
                      {bucket.label}
                      <span className="text-content-muted">
                        {" "}· {bucket.etf_count} ETF ({bucket.contributors.map((x) => x.etf).join(", ")})
                      </span>
                    </span>
                    <span className="tabular text-content-secondary">
                      {pp(bucket.observed_pct, 2)} in portafoglio, di cui {pp(bucket.duplication_pct, 2)} duplicati
                    </span>
                  </li>
                ))}
              </ul>
              <p className="text-label text-content-muted" data-testid="portfolio-duplication-note">
                La duplicazione del portafoglio ({pp(data.duplication_pct, 2)}) conta ogni esposizione
                una volta sola: non è la somma delle coppie, che conterebbe più volte la stessa massa.
              </p>
            </div>
          )}
        </div>
      </SectionCard>
    </div>
  );
}

function ConcentrationRow({ concentration }) {
  const items = [
    ["Esposizioni", concentration.buckets_count ?? "n/d", null],
    ["HHI normalizzato", concentration.hhi_normalised, concentration.hhi_normalised == null],
    ["Top 5", concentration.top5_pct, concentration.top5_pct == null],
    ["Top 10", concentration.top10_pct, concentration.top10_pct == null],
    ["Top 20", concentration.top20_pct, concentration.top20_pct == null],
  ];
  return (
    <dl className="flex flex-wrap gap-x-6 gap-y-2 text-label" data-testid="portfolio-concentration">
      {items.map(([label, value, unavailable], index) => (
        <div key={label} data-testid={`portfolio-concentration-${index}`}>
          <dt className="text-content-muted uppercase tracking-wide">{label}</dt>
          <dd className="tabular text-content">
            {unavailable
              ? "n/d"
              : index === 0
                ? value
                : index === 1
                  ? Number(value).toFixed(4)
                  : `${Number(value).toFixed(1)}%`}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export default PortfolioPanel;
